import { BeachAccess, KeyboardArrowUp } from '@mui/icons-material'
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Box } from '@mui/system'
import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../../config/navigation'
import { getSelectedDate } from '../../../store/redux/reducers/monthPickerSlice'
import { RootState } from '../../../store/redux/store'
import { Column, WorkDay } from '../../../store/types'
import { sumProjectPerMonth } from '../../../store/utils'
import { generateColumns, getDaysOfTheMonth } from '../../../store/utils/worklogUtils'
import { StyledBoxInStickyCell, StyledHeaderCell } from '../../../styles/WorklogTableStyles'
import DecimalHourDurationTooltip from '../../Tooltip/DecimalHourDurationTooltip'
import WorklogTableFooter from './WorklogTableFooter'
import WorklogTableRow from './WorklogTableRow'

export type ColumnLayout = {
  col1Width: number
  col2Width: number
  col3Width: number
  paddingLeft: number
}

type WorklogTableProps = {
  worklog: WorkDay[] | undefined
  vacationId: string
  workHours: number
  vacationHours: number
  loggedHours: number
  remainingHours: number
  isReadOnly: boolean
  cost: number
  isLoading: boolean
  isLeaderOrManagement: boolean
  setIsChangePresent: React.Dispatch<React.SetStateAction<boolean[]>>
}

const WorklogTable = ({
  worklog,
  vacationId,
  workHours,
  vacationHours,
  loggedHours,
  remainingHours,
  isReadOnly,
  cost,
  isLoading,
  isLeaderOrManagement,
  setIsChangePresent,
}: WorklogTableProps) => {
  const [columns, setColumns] = useState<Column[]>([])
  const { t } = useTranslation()
  const theme = useTheme()
  const selectedDate = dayjs(useSelector((state: RootState) => getSelectedDate(state)))
  const daysInAMonth = selectedDate.daysInMonth()
  const vacation = sumProjectPerMonth(worklog, vacationId, true)
  const resize = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()

  useEffect(() => {
    setColumns(generateColumns(worklog, vacationId))
  }, [worklog, vacationId])

  const rowRefs = useRef<(null | { closeRow: () => void })[]>([])

  const collapseAllRows = () => {
    rowRefs.current.forEach(ref => ref?.closeRow?.())
  }

  const columnLayout: ColumnLayout = {
    paddingLeft: !isDownSm ? 16 : 8,
    col1Width: (!isDownSm ? 60 : 40) - (!isDownSm ? 16 : 8),
    col2Width: 80,
    col3Width: !isDownSm ? 80 : 60,
  }

  return (
    <Box sx={{ height: '100%' }}>
      {isDownSm && (
        <Box
          sx={{
            display: 'flex',
            borderColor: 'background.elevation8',
            gap: 1,
            scroll: 'auto',
            mb: 2,
            height: '10%',
          }}
        >
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption">
              {t(`page.worklog.textTile.hoursPerMonth${resize ? 'Short' : ''}`)}
            </Typography>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption">{workHours}</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption">{t('page.worklog.textTile.loggedShort')}</Typography>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <DecimalHourDurationTooltip hours={loggedHours} arrow>
                <Typography variant="caption">{loggedHours}</Typography>
              </DecimalHourDurationTooltip>
            </Box>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption">{t('page.worklog.textTile.remainingShort')}</Typography>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <DecimalHourDurationTooltip hours={remainingHours} arrow>
                <Typography variant="caption">{remainingHours}</Typography>
              </DecimalHourDurationTooltip>
            </Box>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption">{t('page.worklog.textTile.vacation')}</Typography>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption">{vacationHours}</Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption">{t('page.worklog.textTile.costs')}</Typography>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption">{`${cost.toFixed(2)} zł`}</Typography>
            </Box>
          </Box>
        </Box>
      )}
      <TableContainer
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: isDownSm ? '84%' : '100%',
          border: '1px solid',
          color: 'text.main',
          borderColor: 'background.elevation8',
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? 'none' : 'auto',
        }}
      >
        {!isDownSm && (
          <Table sx={{ position: 'sticky', left: 0, zIndex: 1 }}>
            <TableHead sx={{ borderBottom: '3px solid', borderColor: 'background.elevation8' }}>
              <TableRow>
                <TableCell align="center">{t(`page.worklog.textTile.hoursPerMonth${resize ? 'Short' : ''}`)}</TableCell>
                <TableCell align="center">{t('page.worklog.textTile.logged')}</TableCell>
                <TableCell align="center">{t('page.worklog.textTile.remaining')}</TableCell>
                <TableCell align="center">{t('page.worklog.textTile.vacation')}</TableCell>
                <TableCell align="center">{t('page.worklog.textTile.costs')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                bgcolor: 'background.light',
                borderBottom: '3px solid',
                borderColor: 'background.elevation8',
              }}
            >
              <TableRow>
                <TableCell align="center">{workHours}</TableCell>
                <TableCell align="center">
                  <DecimalHourDurationTooltip hours={loggedHours} arrow>
                    <span>{loggedHours}</span>
                  </DecimalHourDurationTooltip>
                </TableCell>
                <TableCell align="center">
                  <DecimalHourDurationTooltip hours={remainingHours} arrow>
                    <span>{remainingHours}</span>
                  </DecimalHourDurationTooltip>
                </TableCell>
                <TableCell align="center">{vacationHours}</TableCell>
                <TableCell align="center">{`${cost.toFixed(2)} zł`}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
        <Table
          sx={{
            width: '100%',
            height: '100%',
          }}
          size="small"
          stickyHeader
          key={isDownSm ? 'sm' : 'lg'}
        >
          <colgroup>
            <col style={{ width: `${columnLayout.col1Width}px` }} />
            <col style={{ width: `${columnLayout.col2Width}px` }} />
            <col style={{ width: `${columnLayout.col3Width}px` }} />
          </colgroup>
          <TableHead sx={{ minHeight: '50px', height: 'auto' }}>
            <TableRow sx={{ backgroundColor: 'background.elevation2' }}>
              <StyledHeaderCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 50,
                  p: 0,
                  height: 'max-content',
                }}
                colSpan={3}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    height: '100%',
                    minHeight: '50px',
                    width: 'fit-content',
                    pl: `${columnLayout.paddingLeft}px`,
                  }}
                >
                  <Box sx={{ width: columnLayout.col1Width }}>
                    <Tooltip title={t('page.worklog.collapseAll')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={collapseAllRows}
                          sx={{
                            color: 'text.base',
                          }}
                          aria-label={t('page.worklog.collapseAll')}
                        >
                          {<KeyboardArrowUp />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <StyledBoxInStickyCell sx={{ width: columnLayout.col2Width }}>
                    {t('page.worklog.table.date')}
                  </StyledBoxInStickyCell>
                  <StyledBoxInStickyCell
                    sx={{
                      justifyContent: 'center',
                      width: columnLayout.col3Width,
                    }}
                  >
                    {t('page.worklog.table.total')}
                  </StyledBoxInStickyCell>
                </Box>
              </StyledHeaderCell>

              {vacation !== 0 && (
                <StyledHeaderCell
                  sx={{
                    width: '50px',
                    borderRight: '1px solid',
                    borderRightColor: 'background.elevation8',
                    paddingX: '16px',
                    textAlign: 'center',
                    lineHeight: 'normal',
                  }}
                >
                  <BeachAccess sx={{ width: 16, height: 16, verticalAlign: 'middle' }} />
                </StyledHeaderCell>
              )}

              {columns.length === 0 ? (
                <StyledHeaderCell
                  component="td"
                  colSpan={1}
                  rowSpan={Number.isNaN(daysInAMonth) ? 0 : daysInAMonth}
                ></StyledHeaderCell>
              ) : (
                columns?.map(column => {
                  const toProjectDetails = () => {
                    navigate(generatePath(navigation.projectDetails, { id: column.projectId }))
                  }
                  const linkToDetailsProps = isLeaderOrManagement
                    ? {
                        sx: {
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                          ':focus': {
                            textDecoration: 'underline',
                          },
                          cursor: 'pointer',
                        },
                        onClick: () => toProjectDetails(),
                        tabIndex: 0,
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            toProjectDetails()
                          }
                        },
                      }
                    : {}
                  return (
                    <StyledHeaderCell
                      key={`header-cell-${column?.id}-${column?.label}-${column.projectId}`}
                      sx={{
                        minWidth: '100px',
                      }}
                      align="center"
                    >
                      <Typography variant={'inherit'} {...linkToDetailsProps}>
                        {column?.label}
                      </Typography>
                    </StyledHeaderCell>
                  )
                })
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {getDaysOfTheMonth(selectedDate).map((day, index) => {
              return (
                <WorklogTableRow
                  key={`${day.id}`}
                  day={day}
                  columns={columns}
                  worklog={worklog}
                  vacationId={vacationId}
                  displayVacation={vacation !== 0}
                  isReadOnly={isReadOnly}
                  setIsChangePresent={setIsChangePresent}
                  ref={el => (rowRefs.current[index] = el)}
                  columnLayout={columnLayout}
                />
              )
            })}
          </TableBody>

          <WorklogTableFooter worklog={worklog} columns={columns} vacationId={vacationId} columnLayout={columnLayout} />
        </Table>
      </TableContainer>
    </Box>
  )
}

export default WorklogTable
